// ViewWriteService.js

/**
 * ViewWriteService.js
 * 
 * Service for writing view data to Firestore.
 */

// Firebase
import { deleteDoc, deleteField, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Services
import { removeViewFromDashboards } from 'services/dashboards/DashboardWriteService';

// Utilities
import { generateKey } from 'utilities/Keys';

// Constants
const VIEWS_COLLECTION = collections.views;

/**
 * Creates a new view in Firestore.
 * 
 * @param {string} userKey - The user key associated with the view.
 * @param {string} type - The type of the view.
 * @param {string} title - The title of the view.
 * @param {object} [options] - Optional view configuration.
 * @param {string} [options.description] - Optional description for the view.
 * @param {number} [options.position] - Optional persisted sort position for the view.
 * @param {string} [options.baseKey] - The base key for view scoping.
 * @param {string} [options.calendarField] - The field name used for calendar event dates.
 * @param {string} [options.boardField] - The field name used for board grouping.
 * @param {object} [options.boardEnumOptions] - Board enum option display overrides.
 * @param {string} [options.timelineField] - The field name used for timeline bars.
 * @param {string} [options.timelineRowMode] - Timeline row layout mode.
 * @param {string} [options.timelineGroupField] - The field name used for timeline grouped rows.
 * @param {boolean} [options.showUnscheduled=true] - Whether timeline should include unscheduled rows.
 * @param {string} [options.preset] - Timeline preset ('timeline' | 'gantt').
 * @param {boolean} [options.showHierarchy] - Whether timeline should render hierarchy rows.
 * @param {boolean} [options.showDependencies] - Whether timeline should render dependency lines.
 * @param {boolean} [options.allowScheduleEdit=false] - Whether timeline bars may edit schedule.
 * @param {object} [options.chartConfig] - Chart configuration for CHART views.
 * @param {object} [options.schedulerConfig] - Scheduler configuration for SCHEDULER views.
 * @param {string[]} [options.properties] - Fields to show in the view.
 * @param {Array<object>} [options.filters] - Filter definitions for the view.
 * @param {Array<object>} [options.sorts] - Sort definitions for the view.
 * @param {boolean} [options.includeDescendants=false] - Whether to include descendant nodes for node-scoped views.
 * @param {boolean} [options.includeSubtitle=false] - Whether to show node subtitles in list views.
 * @param {boolean} [options.includeCover=false] - Whether to show node cover photos in list views.
 * @param {boolean} [options.isDefault=false] - Whether the view is the default view for the user.
 * 
 * @returns {Promise<Object|null>} The created view data or null if userKey is not provided.
 */
export async function createView(userKey, type, title, options = {}) {
  const {
    key,
    description,
    position,
    baseKey,
    boardField,
    boardLayout,
    boardEnumOptions,
    viewGroupId,
    calendarField,
    timelineField,
    timelineRowMode,
    timelineGroupField,
    showUnscheduled,
    preset,
    showHierarchy,
    showDependencies,
    allowScheduleEdit,
    chartConfig,
    schedulerConfig,
    properties,
    nodeType,
    filters,
    sorts,
    includeDescendants,
    includeSubtitle,
    includeDescription,
    includeCover,
    catchAll = false,
    canDelete,
    isDefault = false,
    createdFrom,
    dashboardKey,
  } = options || {};

  // Validate userKey
  if (!userKey) return null;

  // Node-type scoped views are the canonical model.
  if (!nodeType) return null;

  // Generate view key
  const viewKey = typeof key === 'string' && key.trim() ? key.trim() : generateKey();

  // Timestamps
  const now = Timestamp.now();
  const normalizedCreatedFrom = String(createdFrom || '').trim().toUpperCase();
  const allowedCreatedFrom = new Set(['DASHBOARD', 'NODE', 'DATABASE', 'PAGE', 'LAYOUT']);
  const resolvedCreatedFrom = allowedCreatedFrom.has(normalizedCreatedFrom)
    ? normalizedCreatedFrom
    : null;
  const resolvedDashboardKey = resolvedCreatedFrom === 'DASHBOARD'
    ? (String(dashboardKey || '').trim() || null)
    : null;

  // Prepare payload
  const payload = {
    key: viewKey,
    userKey,
    baseKey: baseKey || null,
    scope: 'NODE_TYPE',
    nodeType: nodeType || null,
    description: typeof description === 'string' ? description.trim() || null : null,
    position: Number.isFinite(Number(position)) ? Number(position) : now.toMillis(),
    type,
    title,
    boardField: boardField || null,
    boardLayout: type === 'BOARD'
      ? (String(boardLayout || '').trim().toLowerCase() === 'lanes' ? 'lanes' : 'kanban')
      : null,
    boardEnumOptions: type === 'BOARD' && boardEnumOptions && typeof boardEnumOptions === 'object'
      ? boardEnumOptions
      : null,
    viewGroupId: String(viewGroupId || '').trim() || null,
    calendarField: type === 'CALENDAR' ? (calendarField || null) : null,
    timelineField: type === 'TIMELINE' ? (timelineField || null) : null,
    timelineRowMode: type === 'TIMELINE' ? (timelineRowMode || 'flat') : null,
    timelineGroupField: type === 'TIMELINE' && (timelineRowMode || 'flat') === 'grouped'
      ? (timelineGroupField || null)
      : null,
    showUnscheduled: type === 'TIMELINE'
      ? (showUnscheduled == null ? true : Boolean(showUnscheduled))
      : null,
    preset: type === 'TIMELINE'
      ? (String(preset || '').trim().toLowerCase() === 'gantt' ? 'gantt' : 'timeline')
      : null,
    showHierarchy: type === 'TIMELINE' ? Boolean(showHierarchy) : null,
    showDependencies: type === 'TIMELINE' ? Boolean(showDependencies) : null,
    allowScheduleEdit: type === 'TIMELINE' ? Boolean(allowScheduleEdit) : null,
    chartConfig: type === 'CHART' && chartConfig && typeof chartConfig === 'object'
      ? chartConfig
      : null,
    schedulerConfig: type === 'SCHEDULER' && schedulerConfig && typeof schedulerConfig === 'object'
      ? schedulerConfig
      : null,
    properties: Array.isArray(properties) && properties.length ? properties : null,
    filters: Array.isArray(filters) ? filters : null,
    sorts: Array.isArray(sorts) ? sorts : null,
    includeDescendants: Boolean(includeDescendants),
    includeSubtitle: Boolean(includeSubtitle || includeDescription),
    includeCover: Boolean(includeCover),
    createdFrom: resolvedCreatedFrom,
    dashboardKey: resolvedDashboardKey,
    catchAll: Boolean(catchAll),
    canDelete: typeof canDelete === 'boolean' ? canDelete : true,
    isDefault,
    dateCreated: now,
    dateModified: now,
  };

  // Create view document
  await setDoc(doc(db, VIEWS_COLLECTION, viewKey), payload, { merge: false });

  // Return the created view payload
  return payload;
}

/**
 * Updates an existing view in Firestore.
 * 
 * @param {string} viewKey - The key of the view to update.
 * @param {Object} data - The data to update the view with.
 * 
 * @returns {Promise<void>}
 */
export async function updateView(viewKey, data) {

  // Validate inputs
  if (!viewKey || !data) return;

  // Prepare payload with updated dateModified
  const payload = {
    ...data,
    key: viewKey,
    scope: data?.scope || 'NODE_TYPE',
    collectionKey: deleteField(),
    nodeKey: deleteField(),
    isSystem: deleteField(),
    dateModified: data?.dateModified || Timestamp.now(),
  };

  // Upsert: editing a system-type template from a base must create `_views/{key}`
  // when missing. updateDoc alone fails silently for those keys (doc only exists
  // under `_nodetypes/{type}/views`), so Save never reached the board.
  await setDoc(doc(db, VIEWS_COLLECTION, viewKey), payload, { merge: true });
}

/**
 * Deletes an view by its key.
 * 
 * @param {string} viewKey - The key of the view to delete.
 * 
 * @returns {Promise<void>} - Promise that resolves when the view is deleted.
 */
export async function deleteViewByKey(viewKey) {

  // Validate input
  if (!viewKey) return;

  const ref = doc(db, VIEWS_COLLECTION, viewKey);
  const snap = await getDoc(ref);
  let baseKey = null;
  if (snap.exists()) {
    const data = snap.data() || {};
    baseKey = typeof data?.baseKey === 'string' ? data.baseKey : null;
  }

  // Cascade cleanup first so dashboard references do not dangle if delete succeeds.
  await removeViewFromDashboards({
    viewId: viewKey,
    baseKey,
  });

  // Delete view document
  await deleteDoc(ref);
}
